import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLocationContext } from '../hooks/useLocationContext';
import {
  useLocalGetLocationsQuery,
  useCreateLocationMutation,
  useUpdateLocationMutation,
} from '../store/api/localApi';
import RoleGate from '../components/RoleGate';
import { Location } from '../types';
import {
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  PlusIcon,
  PencilIcon,
  ArrowRightIcon,
  UserGroupIcon,
  RectangleGroupIcon,
} from '@heroicons/react/24/outline';

const Locations: React.FC = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const { isOrgAdmin, isSuperAdmin, isAdmin } = useAuth();
  const { enterLocation, effectiveOrgId } = useLocationContext();

  const { data: allLocations, isLoading } = useLocalGetLocationsQuery(effectiveOrgId || '', {
    skip: !effectiveOrgId,
  });

  // Only org-level roles should access this page
  if (!isOrgAdmin && !isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const locations = allLocations || [];

  const handleEnterLocation = (locationId: string, locationName: string) => {
    enterLocation(locationId, locationName);
  };

  if (isLoading) {
    return (
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-48 bg-gray-200 rounded-lg" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
              Locations
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {locations.length} location{locations.length !== 1 ? 's' : ''} in your organization
            </p>
          </div>
          <RoleGate when={isAdmin}>
            <div className="mt-4 flex md:mt-0 md:ml-4">
              <button
                type="button"
                data-testid="add-location-btn"
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                Add Location
              </button>
            </div>
          </RoleGate>
        </div>

        {/* Location cards */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {locations.map((location) => (
            <div
              key={location.id}
              data-testid={`location-${location.id}`}
              className="bg-white shadow rounded-lg overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <BuildingOfficeIcon className="h-5 w-5 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{location.name}</h3>
                      {location.isActive === false && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          Inactive
                        </span>
                      )}
                    </div>
                  </div>
                  <RoleGate when={isAdmin}>
                    <button
                      data-testid={`edit-location-btn-${location.id}`}
                      onClick={() => setEditingLocation(location)}
                      className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
                      title="Edit location"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                  </RoleGate>
                </div>

                <div className="mt-4 space-y-2 text-sm text-gray-500">
                  {location.address && (
                    <div className="flex items-center gap-2">
                      <MapPinIcon className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{location.address}</span>
                    </div>
                  )}
                  {location.phone && (
                    <div className="flex items-center gap-2">
                      <PhoneIcon className="h-4 w-4 flex-shrink-0" />
                      <span>{location.phone}</span>
                    </div>
                  )}
                  {location.email && (
                    <div className="flex items-center gap-2">
                      <EnvelopeIcon className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{location.email}</span>
                    </div>
                  )}
                </div>

                {/* Quick stats */}
                {location.capacity && (
                  <div className="mt-4 flex items-center gap-4 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <UserGroupIcon className="h-3.5 w-3.5" />
                      Capacity: {location.capacity}
                    </span>
                    {location.ageGroups && location.ageGroups.length > 0 && (
                      <span className="flex items-center gap-1">
                        <RectangleGroupIcon className="h-3.5 w-3.5" />
                        {location.ageGroups.length} age groups
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Enter location button */}
              <div className="border-t border-gray-100 px-6 py-3 bg-gray-50">
                <button
                  data-testid={`enter-location-${location.id}`}
                  onClick={() => handleEnterLocation(location.id, location.name)}
                  className="w-full flex items-center justify-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-800 transition-colors"
                >
                  Manage Location
                  <ArrowRightIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {locations.length === 0 && (
          <div className="mt-8 bg-white shadow rounded-lg text-center py-12">
            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No locations</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by adding your first location.</p>
            <div className="mt-6">
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                Add Location
              </button>
            </div>
          </div>
        )}

        {showAddModal && <AddLocationModal onClose={() => setShowAddModal(false)} />}
        {editingLocation && (
          <EditLocationModal location={editingLocation} onClose={() => setEditingLocation(null)} />
        )}
      </div>
    </div>
  );
};

const AddLocationModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [createLocation, { isLoading }] = useCreateLocationMutation();
  const { effectiveOrgId: activeOrganizationId } = useLocationContext();
  const [form, setForm] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    timezone: 'Europe/Dublin',
    capacity: '',
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    // Mirror the backend's required fields (state + zipCode included). The
    // location-create handler's zod schema rejects without these, so guarding
    // here gives the user an inline message instead of a roundtrip + generic
    // "Validation failed".
    if (
      !form.name ||
      !form.address ||
      !form.city ||
      !form.state ||
      !form.zipCode ||
      !form.timezone
    ) {
      setError('Name, address, city, state, zip code, and timezone are required.');
      return;
    }
    try {
      await createLocation({
        organizationId: activeOrganizationId || '',
        name: form.name,
        address: form.address,
        city: form.city,
        state: form.state,
        zipCode: form.zipCode,
        phone: form.phone || undefined,
        email: form.email || undefined,
        timezone: form.timezone,
        capacity: form.capacity ? parseInt(form.capacity) : undefined,
      }).unwrap();
      onClose();
    } catch (err: any) {
      // Surface zod field errors when the backend returns them, otherwise
      // fall back to the generic message string.
      const data = err?.data;
      if (data?.details && Array.isArray(data.details) && data.details.length > 0) {
        setError(data.details.map((d: any) => `${d.field}: ${d.message}`).join('; '));
      } else {
        setError(data?.message || data?.error || 'Failed to create location');
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" data-testid="add-location-modal">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="relative z-10 bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Add Location</h3>
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Name *</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                placeholder="e.g. Main Campus"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Address *</label>
                <input
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">City *</label>
                <input
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">State *</label>
                <input
                  name="state"
                  value={form.state}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Zip Code *</label>
                <input
                  name="zipCode"
                  value={form.zipCode}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Capacity</label>
                <input
                  name="capacity"
                  type="number"
                  value={form.capacity}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 border border-transparent rounded-md text-sm text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                {isLoading ? 'Creating...' : 'Add Location'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const EditLocationModal: React.FC<{ location: Location; onClose: () => void }> = ({
  location,
  onClose,
}) => {
  const [updateLocation, { isLoading }] = useUpdateLocationMutation();
  const [form, setForm] = useState({
    name: location.name || '',
    address: location.address || '',
    city: (location as any).city || '',
    state: (location as any).state || '',
    zipCode: (location as any).zipCode || '',
    phone: location.phone || '',
    email: location.email || '',
    timezone: location.timezone || 'Europe/Dublin',
    capacity: location.capacity?.toString() || '',
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.name || !form.address) {
      setError('Name and address are required');
      return;
    }
    try {
      await updateLocation({
        locationId: location.id,
        updates: {
          name: form.name,
          address: form.address,
          city: form.city || undefined,
          state: form.state || undefined,
          zipCode: form.zipCode || undefined,
          phone: form.phone || undefined,
          email: form.email || undefined,
          timezone: form.timezone,
          capacity: form.capacity ? parseInt(form.capacity) : undefined,
        },
      }).unwrap();
      onClose();
    } catch (err: any) {
      setError(err.data?.error || 'Failed to update location');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" data-testid="edit-location-modal">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="relative z-10 bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Location</h3>
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Name *</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Address *</label>
                <input
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">City</label>
                <input
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">State *</label>
                <input
                  name="state"
                  value={form.state}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Zip Code *</label>
                <input
                  name="zipCode"
                  value={form.zipCode}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Capacity</label>
                <input
                  name="capacity"
                  type="number"
                  value={form.capacity}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Timezone</label>
              <input
                name="timezone"
                value={form.timezone}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 border border-transparent rounded-md text-sm text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Locations;
